import { decodeBase64, encodeBase64 } from './buffer';
import type * as vscode from './@types/vscode';
import { Disposable } from './dispose';
import serviceWorkerUrl from './sw.js?worker&url';

export function addMatchMediaChangeListener(targetWindow: Window, query: string | MediaQueryList, callback: (this: MediaQueryList, ev: MediaQueryListEvent) => unknown): void {
    if (typeof query === 'string') {
        query = targetWindow.matchMedia(query);
    }
    query.addEventListener('change', callback);
}

let standalone = false;
if (typeof window.matchMedia === 'function') {
    const standaloneMatchMedia = window.matchMedia('(display-mode: standalone) or (display-mode: window-controls-overlay)');
    const fullScreenMatchMedia = window.matchMedia('(display-mode: fullscreen)');
    standalone = standaloneMatchMedia.matches;
    addMatchMediaChangeListener(window, standaloneMatchMedia, ({ matches }) => {
        // entering fullscreen would change standaloneMatchMedia.matches to false
        // if standalone is true (running as PWA) and entering fullscreen, skip this change
        if (standalone && fullScreenMatchMedia.matches) {
            return;
        }
        // otherwise update standalone (browser to PWA or PWA to browser)
        standalone = matches;
    });
}
export function isStandalone(): boolean {
    return standalone;
}

export function isWorkspaceToOpen(uriToOpen: any): uriToOpen is vscode.IWorkspaceToOpen {
    return !!(uriToOpen as vscode.IWorkspaceToOpen).workspaceUri;
}

export function isFolderToOpen(uriToOpen: any): uriToOpen is vscode.IFolderToOpen {
    return !!(uriToOpen as vscode.IFolderToOpen).folderUri;
}

export function ltrim(haystack: string, needle: string): string {
    if (!haystack || !needle) {
        return haystack;
    }

    const needleLen = needle.length;
    if (needleLen === 0 || haystack.length === 0) {
        return haystack;
    }

    let offset = 0;

    while (haystack.indexOf(needle, offset) === offset) {
        offset = offset + needleLen;
    }
    return haystack.substring(offset);
}

interface ISecretStorageCrypto {
    seal(data: string): Promise<string>;
    unseal(data: string): Promise<string>;
}

class TransparentCrypto implements ISecretStorageCrypto {
    async seal(data: string): Promise<string> {
        return data;
    }

    async unseal(data: string): Promise<string> {
        return data;
    }
}

class Base64Crypto implements ISecretStorageCrypto {
    async seal(data: string): Promise<string> {
        return encodeBase64(new TextEncoder().encode(data));
    }

    async unseal(data: string): Promise<string> {
        return new TextDecoder().decode(decodeBase64(data));
    }
}

const enum AESConstants {
    ALGORITHM = 'AES-GCM',
    KEY_LENGTH = 256,
    IV_LENGTH = 12,
}

class NetworkError extends Error {
    constructor(inner: Error) {
        super(inner.message);
        this.name = inner.name;
        this.stack = inner.stack;
    }
}

class ServerKeyedAESCrypto {
    private _serverKey: Uint8Array | undefined;

    /** Gets whether the algorithm is supported; requires a secure context */
    public static supported() {
        return !!crypto.subtle;
    }

    constructor(private readonly authEndpoint: string) { }

    async seal(data: string): Promise<string> {
        // Get a new key and IV on every change, to avoid the risk of reusing the same key and IV pair with AES-GCM
        // (see also: https://developer.mozilla.org/en-US/docs/Web/API/AesGcmParams#properties)
        const iv = window.crypto.getRandomValues(new Uint8Array(AESConstants.IV_LENGTH));
        // crypto.getRandomValues isn't a good-enough PRNG to generate crypto keys, so we need to use crypto.subtle.generateKey and export the key instead
        const clientKeyObj = await window.crypto.subtle.generateKey(
            { name: AESConstants.ALGORITHM as const, length: AESConstants.KEY_LENGTH as const },
            true,
            ['encrypt', 'decrypt'],
        );

        const clientKey = new Uint8Array(await window.crypto.subtle.exportKey('raw', clientKeyObj));
        const key = await this.getKey(clientKey);
        const dataUint8Array = new TextEncoder().encode(data);
        const cipherText: ArrayBuffer = await window.crypto.subtle.encrypt(
            { name: AESConstants.ALGORITHM as const, iv },
            key,
            dataUint8Array,
        );

        // Base64 encode the result and store the ciphertext, the key, and the IV in localStorage
        // Note that the clientKey and IV don't need to be secret
        const result = new Uint8Array([...clientKey, ...iv, ...new Uint8Array(cipherText)]);
        return encodeBase64(result);
    }

    async unseal(data: string): Promise<string> {
        // encrypted should contain, in order: the key (32-byte), the IV for AES-GCM (12-byte) and the ciphertext (which has the GCM auth tag at the end)
        // Minimum length must be 44 (key+IV length) + 16 bytes (1 block encrypted with AES - regardless of key size)
        const dataUint8Array = decodeBase64(data);

        if (dataUint8Array.byteLength < 60) {
            throw Error('Invalid length for the value for credentials.crypto');
        }

        const keyLength = AESConstants.KEY_LENGTH / 8;
        const clientKey = dataUint8Array.slice(0, keyLength);
        const iv = dataUint8Array.slice(keyLength, keyLength + AESConstants.IV_LENGTH);
        const cipherText = dataUint8Array.slice(keyLength + AESConstants.IV_LENGTH);

        // Do the decryption and parse the result as JSON
        const key = await this.getKey(clientKey);
        const decrypted = await window.crypto.subtle.decrypt(
            { name: AESConstants.ALGORITHM as const, iv: iv.buffer },
            key,
            cipherText.buffer,
        );

        return new TextDecoder().decode(new Uint8Array(decrypted));
    }

    /**
     * Given a clientKey, returns the CryptoKey object that is used to encrypt/decrypt the data.
     * The actual key is (clientKey XOR serverKey)
     */
    private async getKey(clientKey: Uint8Array): Promise<CryptoKey> {
        if (!clientKey || clientKey.byteLength !== AESConstants.KEY_LENGTH / 8) {
            throw Error('Invalid length for clientKey');
        }

        const serverKey = await this.getServerKeyPart();
        const keyData = new Uint8Array(AESConstants.KEY_LENGTH / 8);

        for (let i = 0; i < keyData.byteLength; i++) {
            keyData[i] = clientKey[i]! ^ serverKey[i]!;
        }

        return window.crypto.subtle.importKey(
            'raw',
            keyData,
            {
                name: AESConstants.ALGORITHM as const,
                length: AESConstants.KEY_LENGTH as const,
            },
            true,
            ['encrypt', 'decrypt'],
        );
    }

    private async getServerKeyPart(): Promise<Uint8Array> {
        if (this._serverKey) {
            return this._serverKey;
        }

        let attempt = 0;
        let lastError: Error | undefined;

        while (attempt <= 3) {
            try {
                const res = await fetch(this.authEndpoint, { credentials: 'include', method: 'POST' });
                if (!res.ok) {
                    throw new Error(res.statusText);
                }
                const serverKey = new Uint8Array(await res.arrayBuffer());
                if (serverKey.byteLength !== AESConstants.KEY_LENGTH / 8) {
                    throw Error(`The key retrieved by the server is not ${AESConstants.KEY_LENGTH} bit long.`);
                }
                this._serverKey = serverKey;
                return this._serverKey;
            } catch (e) {
                lastError = e instanceof Error ? e : new Error(String(e));
                attempt++;

                // exponential backoff
                await new Promise(resolve => setTimeout(resolve, attempt * attempt * 100));
            }
        }

        if (lastError) {
            throw new NetworkError(lastError);
        }
        throw new Error('Unknown error');
    }
}

export class LocalStorageSecretStorageProvider implements vscode.ISecretStorageProvider {
    private readonly _storageKey = 'secrets.provider';

    private _secretsPromise: Promise<Record<string, string>>;

    type: 'in-memory' | 'persisted' | 'unknown' = 'persisted';

    constructor(
        private readonly crypto: ISecretStorageCrypto,
    ) {
        this._secretsPromise = this.load();
    }

    private async load(): Promise<Record<string, string>> {
        const record = this.loadAuthSessionFromElement();
        // Get the secrets from localStorage
        const encrypted = localStorage.getItem(this._storageKey);
        if (encrypted) {
            try {
                const decrypted = JSON.parse(await this.crypto.unseal(encrypted));
                return { ...record, ...decrypted };
            } catch (err) {
                // TODO: send telemetry
                console.error('Failed to decrypt secrets from localStorage', err);
                if (!(err instanceof NetworkError)) {
                    localStorage.removeItem(this._storageKey);
                }
            }
        }

        return record;
    }

    private loadAuthSessionFromElement(): Record<string, string> {
        let authSessionInfo: (vscode.AuthenticationSessionInfo & { scopes: string[][]; refreshToken: string }) | undefined;
        const authSessionElement = window.document.getElementById('vscode-workbench-auth-session');
        const authSessionElementAttribute = authSessionElement ? authSessionElement.getAttribute('data-settings') : undefined;
        if (authSessionElementAttribute) {
            try {
                authSessionInfo = JSON.parse(authSessionElementAttribute);
            } catch (error) { /* Invalid session is passed. Ignore. */ }
        }

        if (!authSessionInfo) {
            return {};
        }

        const record: Record<string, string> = {};

        // Settings Sync Entry
        // record[`${product.urlProtocol}.loginAccount`] = JSON.stringify(authSessionInfo);

        // Auth extension Entry
        if (authSessionInfo.providerId !== 'gitpod-flex') {
            console.error(`Unexpected auth provider: ${authSessionInfo.providerId}. Expected 'gitpod-flex'.`);
            return record;
        }

        const authAccount = JSON.stringify({ extensionId: 'gitpod.gitpod-flex', key: 'gitpod-flex.auth2.app.gitpod.io' });
        record[authAccount] = JSON.stringify(authSessionInfo.scopes.map(scopes => ({
            id: authSessionInfo.id,
            scopes,
            refreshToken: authSessionInfo.refreshToken,
        })));

        return record;
    }

    async get(key: string): Promise<string | undefined> {
        const secrets = await this._secretsPromise;
        return secrets[key];
    }

    async set(key: string, value: string): Promise<void> {
        const secrets = await this._secretsPromise;
        secrets[key] = value;
        this._secretsPromise = Promise.resolve(secrets);
        this.save();
    }

    async delete(key: string): Promise<void> {
        const secrets = await this._secretsPromise;
        delete secrets[key];
        this._secretsPromise = Promise.resolve(secrets);
        this.save();
    }

    private async save(): Promise<void> {
        try {
            const encrypted = await this.crypto.seal(JSON.stringify(await this._secretsPromise));
            localStorage.setItem(this._storageKey, encrypted);
        } catch (err) {
            console.error(err);
        }
    }
}

////////////////////////////////////////////////////////////////////////////////////

export function showSplash() {
    const rawData = localStorage.getItem('monaco-parts-splash');
    if (!rawData) {
        return false;
    }

    let data: vscode.IPartsSplash;
    try {
        data = JSON.parse(rawData);
    } catch {
        return false;
    }
    if (!data || typeof data !== 'object' || !data.layoutInfo) {
        return false;
    }

    const style = document.createElement('style');
    style.className = 'initialShellColors';
    window.document.head.appendChild(style);
    style.textContent = `
body {
    background-color: ${data.colorInfo.editorBackground};
    color: ${data.colorInfo.foreground};
    margin: 0;
    padding: 0;
}
`;

    if (data?.layoutInfo) {
        const { layoutInfo, colorInfo } = data;

        const splash = document.createElement('div');
        splash.id = 'monaco-parts-splash';
        splash.className = data.baseTheme ?? '';

        // ensure there is enough space
        layoutInfo.auxiliarySideBarWidth = Math.min(layoutInfo.auxiliarySideBarWidth, window.innerWidth - (layoutInfo.activityBarWidth + layoutInfo.editorPartMinWidth + layoutInfo.sideBarWidth));
        layoutInfo.sideBarWidth = Math.min(layoutInfo.sideBarWidth, window.innerWidth - (layoutInfo.activityBarWidth + layoutInfo.editorPartMinWidth + layoutInfo.auxiliarySideBarWidth));

        // part: title
        if (layoutInfo.titleBarHeight > 0) {
            const titleDiv = document.createElement('div');
            titleDiv.style.position = 'absolute';
            titleDiv.style.width = '100%';
            titleDiv.style.height = `${layoutInfo.titleBarHeight}px`;
            titleDiv.style.left = '0';
            titleDiv.style.top = '0';
            titleDiv.style.backgroundColor = `${colorInfo.titleBarBackground}`;
            (titleDiv.style as any)['-webkit-app-region'] = 'drag';
            splash.appendChild(titleDiv);

            if (colorInfo.titleBarBorder) {
                const titleBorder = document.createElement('div');
                titleBorder.style.position = 'absolute';
                titleBorder.style.width = '100%';
                titleBorder.style.height = '1px';
                titleBorder.style.left = '0';
                titleBorder.style.bottom = '0';
                titleBorder.style.borderBottom = `1px solid ${colorInfo.titleBarBorder}`;
                titleDiv.appendChild(titleBorder);
            }
        }

        // part: activity bar
        if (layoutInfo.activityBarWidth > 0) {
            const activityDiv = document.createElement('div');
            activityDiv.style.position = 'absolute';
            activityDiv.style.width = `${layoutInfo.activityBarWidth}px`;
            activityDiv.style.height = `calc(100% - ${layoutInfo.titleBarHeight + layoutInfo.statusBarHeight}px)`;
            activityDiv.style.top = `${layoutInfo.titleBarHeight}px`;
            if (layoutInfo.sideBarSide === 'left') {
                activityDiv.style.left = '0';
            } else {
                activityDiv.style.right = '0';
            }
            activityDiv.style.backgroundColor = `${colorInfo.activityBarBackground}`;
            splash.appendChild(activityDiv);

            if (colorInfo.activityBarBorder) {
                const activityBorderDiv = document.createElement('div');
                activityBorderDiv.style.position = 'absolute';
                activityBorderDiv.style.width = '1px';
                activityBorderDiv.style.height = '100%';
                activityBorderDiv.style.top = '0';
                if (layoutInfo.sideBarSide === 'left') {
                    activityBorderDiv.style.right = '0';
                    activityBorderDiv.style.borderRight = `1px solid ${colorInfo.activityBarBorder}`;
                } else {
                    activityBorderDiv.style.left = '0';
                    activityBorderDiv.style.borderLeft = `1px solid ${colorInfo.activityBarBorder}`;
                }
                activityDiv.appendChild(activityBorderDiv);
            }
        }

        // part: side bar (only when opening workspace/folder)
        if (layoutInfo.sideBarWidth > 0) {
            const sideDiv = document.createElement('div');
            sideDiv.style.position = 'absolute';
            sideDiv.style.width = `${layoutInfo.sideBarWidth}px`;
            sideDiv.style.height = `calc(100% - ${layoutInfo.titleBarHeight + layoutInfo.statusBarHeight}px)`;
            sideDiv.style.top = `${layoutInfo.titleBarHeight}px`;
            if (layoutInfo.sideBarSide === 'left') {
                sideDiv.style.left = `${layoutInfo.activityBarWidth}px`;
            } else {
                sideDiv.style.right = `${layoutInfo.activityBarWidth}px`;
            }
            sideDiv.style.backgroundColor = `${colorInfo.sideBarBackground}`;
            splash.appendChild(sideDiv);

            if (colorInfo.sideBarBorder) {
                const sideBorderDiv = document.createElement('div');
                sideBorderDiv.style.position = 'absolute';
                sideBorderDiv.style.width = '1px';
                sideBorderDiv.style.height = '100%';
                sideBorderDiv.style.top = '0';
                sideBorderDiv.style.right = '0';
                if (layoutInfo.sideBarSide === 'left') {
                    sideBorderDiv.style.borderRight = `1px solid ${colorInfo.sideBarBorder}`;
                } else {
                    sideBorderDiv.style.left = '0';
                    sideBorderDiv.style.borderLeft = `1px solid ${colorInfo.sideBarBorder}`;
                }
                sideDiv.appendChild(sideBorderDiv);
            }
        }

        // part: auxiliary sidebar
        if (layoutInfo.auxiliarySideBarWidth > 0) {
            const auxSideDiv = document.createElement('div');
            auxSideDiv.style.position = 'absolute';
            auxSideDiv.style.width = `${layoutInfo.auxiliarySideBarWidth}px`;
            auxSideDiv.style.height = `calc(100% - ${layoutInfo.titleBarHeight + layoutInfo.statusBarHeight}px)`;
            auxSideDiv.style.top = `${layoutInfo.titleBarHeight}px`;
            if (layoutInfo.sideBarSide === 'left') {
                auxSideDiv.style.right = '0';
            } else {
                auxSideDiv.style.left = '0';
            }
            auxSideDiv.style.backgroundColor = `${colorInfo.sideBarBackground}`;
            splash.appendChild(auxSideDiv);

            if (colorInfo.sideBarBorder) {
                const auxSideBorderDiv = document.createElement('div');
                auxSideBorderDiv.style.position = 'absolute';
                auxSideBorderDiv.style.width = '1px';
                auxSideBorderDiv.style.height = '100%';
                auxSideBorderDiv.style.top = '0';
                if (layoutInfo.sideBarSide === 'left') {
                    auxSideBorderDiv.style.left = '0';
                    auxSideBorderDiv.style.borderLeft = `1px solid ${colorInfo.sideBarBorder}`;
                } else {
                    auxSideBorderDiv.style.right = '0';
                    auxSideBorderDiv.style.borderRight = `1px solid ${colorInfo.sideBarBorder}`;
                }
                auxSideDiv.appendChild(auxSideBorderDiv);
            }
        }

        // part: statusbar
        if (layoutInfo.statusBarHeight > 0) {
            const statusDiv = document.createElement('div');
            statusDiv.style.position = 'absolute';
            statusDiv.style.width = '100%';
            statusDiv.style.height = `${layoutInfo.statusBarHeight}px`;
            statusDiv.style.bottom = '0';
            statusDiv.style.left = '0';
            statusDiv.style.backgroundColor = colorInfo.statusBarBackground || '';
            splash.appendChild(statusDiv);

            if (colorInfo.statusBarBorder) {
                const statusBorderDiv = document.createElement('div');
                statusBorderDiv.style.position = 'absolute';
                statusBorderDiv.style.width = '100%';
                statusBorderDiv.style.height = '1px';
                statusBorderDiv.style.top = '0';
                statusBorderDiv.style.borderTop = `1px solid ${colorInfo.statusBarBorder}`;
                statusDiv.appendChild(statusBorderDiv);
            }
        }

        window.document.body.appendChild(splash);
        splash.classList.add('loading');
    }
}

// Function to fetch the latest VS Code commit version
export async function fetchLatestCommit(): Promise<string> {
    try {
        const response = await fetch('https://update.code.visualstudio.com/api/commits/stable/server-linux-x64-web');
        if (!response.ok) {
            throw new Error(`Failed to fetch latest commit: ${response.statusText}`);
        }
        const commits = await response.json();

        // The response is an array of strings, return the first value
        if (Array.isArray(commits) && commits.length > 0) {
            return commits[0].trim();
        } else {
            throw new Error('Invalid response format from VS Code API');
        }
    } catch (e) {
        console.error('Error fetching latest vscode commit:', e);
        throw e;
    }
}

const VSCODE_CDN_URL_BASE = 'https://main.vscode-gitpod-cdn.com/vscode-cdn.net';

// Function to dynamically load VS Code resources
export async function loadVSCodeResources(commit: string): Promise<typeof import('./@types/vscode')> {
    try {
        const trustedTypesPolicy = window.trustedTypes?.createPolicy('amdLoader', {
            createScriptURL(url, base) {
                if (url.startsWith(`${VSCODE_CDN_URL_BASE}/stable/${commit}`)) {
                    return base ? new URL(url, base) as any : url;
                }
                if (url.startsWith(`${VSCODE_CDN_URL_BASE}/nlsmetadata/${commit}`)) {
                    return base ? new URL(url, base) as any : url;
                }
                if (url.startsWith('/static/')) {
                    return base ? new URL(url, base) as any : url;
                }
                if (url === serviceWorkerUrl) {
                    return base ? new URL(url, base) as any : url;
                }
                throw new Error(`Invalid script url: ${url}`);
            },
        });

        (globalThis as any)._VSCODE_WEB_BOOTSTRAP_ = {
            quality: 'stable',
            commit,
            baseUrl: '${VSCODE_CDN_URL_BASE}/stable/${commit}',
            trustedTypesPolicy,
        };

        // Create a promise for CSS loading
        const cssLoadPromise = new Promise<void>((resolve, reject) => {
            // Load CSS
            const cssLink = document.createElement('link');
            cssLink.rel = 'stylesheet';
            cssLink.href = `${VSCODE_CDN_URL_BASE}/stable/${commit}/out/vs/workbench/workbench.web.main.internal.css`;
            cssLink.onload = () => resolve();
            cssLink.onerror = e => reject(new Error(`Failed to load CSS: ${e}`));
            document.head.appendChild(cssLink);
        });

        // Create a promise for NLS messages loading
        const nlsLoadPromise = new Promise<void>((resolve, reject) => {
            // Load NLS messages
            const nlsScript = document.createElement('script');
            nlsScript.async = true;
            nlsScript.src = trustedTypesPolicy?.createScriptURL(`${VSCODE_CDN_URL_BASE}/nlsmetadata/${commit}/nls.messages.js`) ?? `${VSCODE_CDN_URL_BASE}/nlsmetadata/${commit}/nls.messages.js`;
            nlsScript.onload = () => resolve();
            nlsScript.onerror = e => reject(new Error(`Failed to load NLS messages: ${e}`));
            document.head.appendChild(nlsScript);
        });

        // Wait for both CSS and NLS to load
        await Promise.all([cssLoadPromise, nlsLoadPromise]);
        console.log('CSS and NLS messages loaded successfully');

        const mainJsUrl = `${VSCODE_CDN_URL_BASE}/stable/${commit}/out/vs/workbench/workbench.web.main.internal.js`;
        (globalThis as any)._VSCODE_FILE_ROOT = `${VSCODE_CDN_URL_BASE}/stable/${commit}/out/`;
        (globalThis as any)._VSCODE_WEB_PACKAGE_TTP = trustedTypesPolicy;

        return (await import(/* @vite-ignore */ mainJsUrl));
    } catch (error) {
        console.error('Error loading VS Code resources:', error);
        throw error;
    }
}

export async function getVSCodeCreationOptions(vscode: typeof import('./@types/vscode'), folderUri?: vscode.URI): Promise<Partial<vscode.IWorkbenchConstructionOptions>> {
    class LocalStorageURLCallbackProvider extends Disposable implements vscode.IURLCallbackProvider {
        private static REQUEST_ID = 0;

        private static QUERY_KEYS: ('scheme' | 'authority' | 'path' | 'query' | 'fragment')[] = [
            'scheme',
            'authority',
            'path',
            'query',
            'fragment',
        ];

        private readonly _onCallback = this._register(new vscode.Emitter<vscode.URI>());
        readonly onCallback = this._onCallback.event;

        private pendingCallbacks = new Set<number>();
        private lastTimeChecked = Date.now();
        private checkCallbacksTimeout: unknown | undefined = undefined;
        private onDidChangeLocalStorageDisposable: vscode.IDisposable | undefined;

        constructor(private readonly _callbackRoute: string) {
            super();
        }

        create(options: Partial<vscode.UriComponents> = {}): vscode.URI {
            const id = ++LocalStorageURLCallbackProvider.REQUEST_ID;
            const queryParams: string[] = [`vscode-reqid=${id}`];

            for (const key of LocalStorageURLCallbackProvider.QUERY_KEYS) {
                const value = options[key];

                if (value) {
                    queryParams.push(`vscode-${key}=${encodeURIComponent(value)}`);
                }
            }

            if (!(options.authority === 'vscode.github-authentication' && options.path === '/dummy')) {
                const key = `vscode-web.url-callbacks[${id}]`;
                localStorage.removeItem(key);

                this.pendingCallbacks.add(id);
                this.startListening();
            }

            return vscode.URI.parse(window.location.href).with({ path: this._callbackRoute, query: queryParams.join('&') });
        }

        private startListening(): void {
            if (this.onDidChangeLocalStorageDisposable) {
                return;
            }

            const fn = () => this.onDidChangeLocalStorage();
            window.addEventListener('storage', fn);
            this.onDidChangeLocalStorageDisposable = { dispose: () => window.removeEventListener('storage', fn) };
        }

        private stopListening(): void {
            this.onDidChangeLocalStorageDisposable?.dispose();
            this.onDidChangeLocalStorageDisposable = undefined;
        }

        // this fires every time local storage changes, but we
        // don't want to check more often than once a second
        private async onDidChangeLocalStorage(): Promise<void> {
            const ellapsed = Date.now() - this.lastTimeChecked;

            if (ellapsed > 1000) {
                this.checkCallbacks();
            } else if (this.checkCallbacksTimeout === undefined) {
                this.checkCallbacksTimeout = setTimeout(() => {
                    this.checkCallbacksTimeout = undefined;
                    this.checkCallbacks();
                }, 1000 - ellapsed);
            }
        }

        private checkCallbacks(): void {
            let pendingCallbacks: Set<number> | undefined;

            for (const id of this.pendingCallbacks) {
                const key = `vscode-web.url-callbacks[${id}]`;
                const result = localStorage.getItem(key);

                if (result !== null) {
                    try {
                        this._onCallback.fire(vscode.URI.revive(JSON.parse(result)));
                    } catch (error) {
                        console.error(error);
                    }

                    pendingCallbacks = pendingCallbacks ?? new Set(this.pendingCallbacks);
                    pendingCallbacks.delete(id);
                    localStorage.removeItem(key);
                }
            }

            if (pendingCallbacks) {
                this.pendingCallbacks = pendingCallbacks;

                if (this.pendingCallbacks.size === 0) {
                    this.stopListening();
                }
            }

            this.lastTimeChecked = Date.now();
        }
    }

    class WorkspaceProvider2 implements vscode.IWorkspaceProvider {
        static QUERY_PARAM_PAYLOAD = 'payload';

        private _payload: any;
        readonly trusted = true;

        constructor(
            readonly workspace: vscode.IWorkspace,
        ) {
        }

        get payload() {
            if (this._payload === undefined) {
                let newPayload = null;
                new URL(window.location.href).searchParams.forEach((value, key) => {
                    if (key === WorkspaceProvider2.QUERY_PARAM_PAYLOAD) {
                        try {
                            newPayload = JSON.parse(value);
                        } catch (err) {
                            console.error(err);
                        }
                    }
                });
                this._payload = newPayload;
            }
            return this._payload ?? undefined;
        }

        async open(workspace: vscode.IWorkspace, options?: { reuse?: boolean; payload?: object }) {
            let targetHref = this.createTargetHref(workspace);
            if (!targetHref && workspace === undefined) {
                const targetUrl = new URL(window.location.href);
                targetUrl.pathname = '/';
                targetHref = targetUrl.toString();
            }
            if (targetHref) {
                if (options?.payload) {
                    const targetUrl = new URL(targetHref);
                    targetUrl.searchParams.set(WorkspaceProvider2.QUERY_PARAM_PAYLOAD, JSON.stringify(options.payload)),
                    targetHref = targetUrl.toString();
                }
                if (options?.reuse) {
                    window.location.href = targetHref;
                    return true;
                }
                let result;
                if (isStandalone()) {
                    result = window.open(targetHref, '_blank', 'toolbar=no'); // ensures to open another 'standalone' window!
                } else {
                    result = window.open(targetHref);
                }

                return !!result;
            }
            throw new Error('Not implemented');
        }

        private createTargetHref(workspace: vscode.IWorkspace): string | undefined {
            if (!workspace) {
                return undefined;
            }

            const uri = isWorkspaceToOpen(workspace) ? workspace.workspaceUri : workspace.folderUri;
            const [type, dest] = uri.authority.split('+');
            if (type !== 'environment') {
                return undefined;
            }

            const targetUrl = new URL(document.location.href);
            targetUrl.search = uri?.query ? `?${uri.query}` : '';
            targetUrl.hash = uri?.fragment ? `#${uri?.fragment}` : '';
            targetUrl.pathname = `environment/${dest}${uri.path || ''}`;

            return targetUrl.toString();
        }
    }

    return {
        settingsSyncOptions: { enabled: true },
        workspaceProvider: new WorkspaceProvider2(folderUri ? { folderUri } : undefined),
        urlCallbackProvider: new LocalStorageURLCallbackProvider('/callback.html'),
        secretStorageProvider: new LocalStorageSecretStorageProvider(new Base64Crypto()),
    };
}
